import { useCallback, useState } from "react";

import api from "@services/api";
import { useParams } from "@hooks/useParams";
import { readZoneOverlays } from "@components/Map/TravelMap";
import { ParamsNames } from "@shared/constants/params-names.enum";
import { PolygonOptions, PolylineOptions } from "@shared/interfaces/map.interface";
import { Travel } from "@shared/interfaces/travel.interface";

/** What the travel map draws besides the tracker records: the travel's places, the client's zones and radius */
export interface TravelCoordinateViewMapData {
  /** The travel with its vehicle (states and alerts) and its discharge points; null until it loads */
  travel: Travel | null;
  /** The client's default construction radius, for jobsites without their own */
  defaultRadiusMeters: number | null;
  zonePolygons: PolygonOptions[];
  zonePolylines: PolylineOptions[];
}

const EMPTY: TravelCoordinateViewMapData = { travel: null, defaultRadiusMeters: null, zonePolygons: [], zonePolylines: [] };

/**
 * Loads what the travel detail map needs around the coordinate records of a travel: the travel (destination,
 * plants, states, alerts and discharges), the ZMRC zones and the default construction radius. Whatever fails to
 * load is left out, and the map still draws the route.
 */
const useTravelCoordinateViewMap = () => {
  const { getParamsObj } = useParams();
  const [data, setData] = useState<TravelCoordinateViewMapData>(EMPTY);
  const [loading, setLoading] = useState(false);

  const fetchMapData = useCallback(async (idTravel: string) => {
    setData(EMPTY);
    setLoading(true);

    const readTravel = async (): Promise<Travel | null> => {
      try {
        const [travelRes, statesRes, alertsRes] = await Promise.all([
          api.get(`travels/read/${idTravel}`).then((r) => r.data),
          api.get(`vehicles/states/get-filtered-by-travel/${idTravel}`).then((r) => r.data),
          api.post("alerts/read/", { idTravel }).then((r) => r.data)
        ]);
        const travel: Travel = {
          ...travelRes.result,
          vehicle: { ...travelRes.result?.vehicle, states: statesRes.result || [], alerts: alertsRes.result || [] }
        };

        // Discharge points, drawn in or out of the jobsite
        if (travel.destination) {
          const coordsRes = (await api.get(`coords/read/${idTravel}`)).data;

          if (coordsRes?.status === "success") travel.coords = coordsRes.result;
        }

        return travel;
      } catch (error) {
        return null;
      }
    };
    const readRadius = async (): Promise<number | null> => {
      try {
        const params = await getParamsObj();

        return Number(params[ParamsNames.DEFAULT_CONSTRUCTION_RADIUS]) || null;
      } catch (error) {
        return null;
      }
    };

    const [travel, defaultRadiusMeters, zones] = await Promise.all([readTravel(), readRadius(), readZoneOverlays()]);

    setData({ travel, defaultRadiusMeters, zonePolygons: zones.polygons, zonePolylines: zones.polylines });
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetMapData = useCallback(() => {
    setData(EMPTY);
    setLoading(false);
  }, []);

  return { ...data, loading, fetchMapData, resetMapData };
};

export default useTravelCoordinateViewMap;
